import {
  AssociationEntity,
  IRefundSchema,
  REFUND_STATUS_DISPLAY,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetAccountById } from '../../../api/accountsService';
import { accountServiceQueryKeys } from '../../../api/queryKeysService';
import {
  useGetRefundById,
  usePrintRefundToHtml,
} from '../../../api/refundService';
import {
  DynamicLabelValueBanner,
  LabelValueLinkItem,
} from '../../../components/DynamicLabelValueBanner/DynamicLabelValueBanner';
import { ExternalAssociationsIcons } from '../../../components/ExternalAssociations/ExternalAssociationsIcons';
import {
  MBox,
  MPageContainer,
  MPageHeader,
  MSpinner,
} from '../../../components/Monetize';
import { HtmlIFrameContentRenderer } from '../../../components/Monetize/HtmlIFrameContentRenderer';
import { PaymentMethodLookupPopover } from '../../../components/Monetize/LookupPopover';
import { ROUTES } from '../../../constants';
import { RefundActions } from './RefundActions';

const refundReadableErrorMessage: { [key: string]: string } = {
  lost_or_stolen_card: 'The card was reported lost or stolen.',
  expired_or_canceled_card: 'The card has expired or was canceled.',
  charge_for_pending_refund_disputed:
    'The charge for the pending refund was disputed.',
  insufficient_funds: 'There are insufficient funds available.',
  declined: 'The transaction was declined.',
  merchant_request: 'Refund requested by the merchant.',
  unknown: 'The reason for the refund failure is unknown.',
};

export const RefundPage: FC = () => {
  const params = useParams();
  const queryClient = useQueryClient();

  const refundId = params?.refundId || '';
  const accountId = params?.accountId || '';

  /**
   * Manual cache check to prevent duplicate API calls.
   * While React Query should automatically handle cached data, it doesn't always work
   * reliably in this scenario when navigating from IdPrefixNavigator. We manually check
   * the cache and use the data as initialData while disabling the query to ensure
   * no duplicate requests are made.
   */
  const existingCacheData = refundId
    ? queryClient.getQueryData<IRefundSchema>(
        accountServiceQueryKeys.refunds.refundById(refundId),
      )
    : undefined;

  const { data: refund, isLoading: isRefundIdLoading } = useGetRefundById(
    refundId,
    {
      enabled: !!refundId && !existingCacheData,
      meta: {
        showErrorToast: true,
        navigateTo: accountId
          ? ROUTES.getAccountPaymentsRoute(accountId)
          : null,
      },
    },
  );

  const { data: accountDetails } = useGetAccountById(accountId, {
    enabled: !!accountId,
    refetchOnWindowFocus: false,
  });

  const { data, isLoading, isRefetching, error, isPending } =
    usePrintRefundToHtml({
      refundId,
    });

  const DisplayBannerComponent = useMemo(() => {
    if (!isRefundIdLoading) {
      if (isEmpty(refund)) {
        return null;
      }

      const items: LabelValueLinkItem[] = [
        {
          label: 'Created Date',
          value: toDateShort(refund?.createDate),
        },
        {
          label: 'Last Modified Date',
          value: toDateShort(refund?.modifyDate),
        },
        {
          label: 'Settled Date',
          value: toDateShort(refund?.settledDate),
        },
      ];

      refund?.paymentMethodId &&
        items.push({
          label: 'Payment Method',
          value: refund.paymentMethodId,
          node: (
            <PaymentMethodLookupPopover
              value={refund.paymentMethodId}
              accountId={accountId}
            />
          ),
        });

      refund?.gatewayReference &&
        items.push({
          label: 'Gateway Reference No.',
          value: refund?.gatewayReference,
        });
      refund?.description &&
        items.push({
          label: 'Description',
          value: refund?.description,
        });
      refund?.errorCode &&
        items.push({
          label: 'Error Code',
          value: refund?.errorCode,
        });
      refund.errorMessage &&
        items.push({
          label: 'Error Message',
          value: refundReadableErrorMessage[refund.errorMessage],
        });

      return <DynamicLabelValueBanner title="Refund Details" items={items} />;
    }
    return (
      <MBox w="100%" display="flex" alignItems="center" justifyContent="center">
        <MSpinner size="sm" />
      </MBox>
    );
  }, [isRefundIdLoading, refund, accountId]);

  if (error) {
    // TODO: add error state
    return null;
  }

  return (
    <MPageContainer height="auto">
      <MPageHeader
        hasBackButton
        title="View Refund"
        status={refund?.status ? REFUND_STATUS_DISPLAY[refund?.status] : ''}
        parentLink={ROUTES.getAccountPaymentsRoute(accountId)}
        parentLinkTitle={accountId && accountDetails?.accountName}
        copyUrl
        id={refund?.id}
        recordAuditInfo={refund}
        entityJson={refund}
        extraBadges={[
          <ExternalAssociationsIcons
            key="ExternalAssociationsIcons"
            entity={AssociationEntity.REFUND}
            recordId={refund?.id || refundId}
          />,
        ]}
      >
        {refund && <RefundActions refundId={refund.id} />}
      </MPageHeader>

      <MBox
        width="100%"
        px={[1, 1, 8]}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        {DisplayBannerComponent}
        <MBox
          width="100%"
          mt={6}
          borderWidth={0.5}
          borderColor="tBlue.hover"
          background="tWhite.titanWhite"
        >
          <HtmlIFrameContentRenderer
            id={'refund-content'}
            html={data}
            isLoading={isLoading}
            isReFetching={isPending || isRefetching}
          ></HtmlIFrameContentRenderer>
        </MBox>
      </MBox>
    </MPageContainer>
  );
};
